import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Modal, Alert, Spin, Button, Typography, Tag, List } from 'antd'
import {
  CloudDownloadOutlined,
  ExclamationCircleOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { useSelector } from 'react-redux'

const { Text, Paragraph } = Typography

const AudioVideoModal = ({ visible, onClose, mediaType }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [fileExists, setFileExists] = useState(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [activeChapter, setActiveChapter] = useState(null)
  const audioElRef = useRef(null)

  // Access the media URLs from Redux state
  const { mediaUrls } = useSelector(state => state.mediaState)

  // Build the list of items for the selected media type. Falls back to the
  // legacy single-URL field for older callers that still set audio/video.
  const items = useMemo(() => {
    if (!mediaUrls) return []
    if (mediaType === 'audio') {
      if (Array.isArray(mediaUrls.audios) && mediaUrls.audios.length > 0) return mediaUrls.audios
      return mediaUrls.audio ? [{ url: mediaUrls.audio, filename: 'audio', questionId: null }] : []
    }
    if (mediaType === 'video') {
      if (Array.isArray(mediaUrls.videos) && mediaUrls.videos.length > 0) return mediaUrls.videos
      return mediaUrls.video ? [{ url: mediaUrls.video, filename: 'video', questionId: null }] : []
    }
    return []
  }, [mediaUrls, mediaType])

  // Media the retention policy has expired. Counted separately so an empty
  // player reads as "removed by policy" rather than the generic CORS/server
  // troubleshooting error, which would send an admin chasing a non-problem.
  const expiredCount = mediaUrls?.expiredCount || 0
  const totalCount = items.length
  const safeIndex = totalCount > 0 ? Math.min(activeIndex, totalCount - 1) : 0
  const currentItem = items[safeIndex] || null
  const mediaUrl = currentItem?.url || null
  // AI Interview session audio only — jump-to-question chapter markers.
  const chapters = Array.isArray(currentItem?.chapters) ? currentItem.chapters : []

  // Reset to the first item whenever the modal opens or the media type changes.
  useEffect(() => {
    if (visible) {
      setActiveIndex(0)
      setActiveChapter(null)
    }
  }, [visible, mediaType])

  const formatOffset = (ms) => {
    const totalSeconds = Math.max(0, Math.round(ms / 1000))
    const mins = Math.floor(totalSeconds / 60)
    const secs = totalSeconds % 60
    return `${mins}:${String(secs).padStart(2, '0')}`
  }

  const jumpToChapter = (chapter) => {
    const el = audioElRef.current
    if (!el) return
    el.currentTime = chapter.offsetMs / 1000
    el.play().catch(() => {})
    setActiveChapter(chapter.n)
  }

  // Validate the currently-active media URL.
  useEffect(() => {
    if (visible) {
      setLoading(true)
      setError(null)
      if (mediaUrl) {
        setFileExists(true)
      } else {
        setError(
          expiredCount > 0
            ? null
            : `No ${mediaType} URL available for this assessment`
        )
        setLoading(false)
        setFileExists(false)
      }
    }
  }, [visible, mediaUrl, mediaType, expiredCount])

  const handleLoadedData = () => {
    setLoading(false)
  }

  const handleError = (e) => {
    console.error('Media error:', e)
    setLoading(false)
    setError(`Failed to load ${mediaType}. This could be due to CORS restrictions or the file might be in an unsupported format.`)
  }

  const getDirectMediaUrl = () => {
    if (mediaUrl) {
      window.open(mediaUrl, '_blank')
    }
  }

  const goPrev = () => {
    if (totalCount > 1 && safeIndex > 0) {
      setActiveIndex(safeIndex - 1)
      setLoading(true)
      setError(null)
    }
  }

  const goNext = () => {
    if (totalCount > 1 && safeIndex < totalCount - 1) {
      setActiveIndex(safeIndex + 1)
      setLoading(true)
      setError(null)
    }
  }

  const titleLabel = mediaType === 'audio' ? 'Audio' : 'Video'
  const itemLabel = mediaType === 'audio' ? 'Audio' : 'Video'
  const showMultiNav = totalCount > 1

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button
          key="directUrl"
          type="primary"
          onClick={getDirectMediaUrl}
          disabled={!mediaUrl}
          icon={<CloudDownloadOutlined />}
        >
          Open Direct URL
        </Button>,
      ]}
      destroyOnClose
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span>{`${titleLabel} Recording`}</span>
          {showMultiNav && (
            <Tag color="blue" style={{ marginLeft: 0 }}>
              {`${itemLabel} ${safeIndex + 1} of ${totalCount}`}
            </Tag>
          )}
        </div>
      }
      width={mediaType === 'audio' ? 500 : 800}
      centered
    >
      {showMultiNav && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Candidate submitted ${totalCount} ${itemLabel.toLowerCase()} responses.`}
          description="Use the navigation buttons below to switch between responses."
        />
      )}

      {loading && fileExists !== false && (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Spin tip={`Loading ${mediaType}...`} />
          <Paragraph style={{ marginTop: 15 }}>
            If loading takes too long, try using the &quot;Open Direct URL&quot; button below.
          </Paragraph>
        </div>
      )}

      {!mediaUrl && expiredCount > 0 && (
        <Alert
          message="Recording expired"
          description={`This ${mediaType} was removed under the media retention policy. The candidate's score and assessment record are unaffected.`}
          type="info"
          showIcon
        />
      )}

      {error && (
        <Alert
          message="Media Error"
          description={
            <div>
              <p>{error}</p>
              <p>
                <ExclamationCircleOutlined style={{ marginRight: 5 }} />
                <Text strong>Troubleshooting:</Text>
              </p>
              <ul>
                <li>Ensure the file exists in the <Text code>media_uploads</Text> directory on the server</li>
                <li>Check that the backend server is running and properly configured for CORS</li>
                <li>Try clicking &quot;Open Direct URL&quot; below to see if direct access works</li>
              </ul>
              {mediaUrl && <Text type="secondary">Media URL: {mediaUrl}</Text>}
            </div>
          }
          type="error"
          showIcon
        />
      )}

      {mediaUrl && fileExists !== false ? (
        mediaType === 'audio' ? (
          <>
            <audio
              key={mediaUrl}
              ref={audioElRef}
              controls
              autoPlay
              style={{ width: '100%', display: loading ? 'none' : 'block' }}
              onLoadedData={handleLoadedData}
              onError={handleError}
              crossOrigin="anonymous"
            >
              <source src={mediaUrl} type="audio/webm" />
              <source src={mediaUrl} type="audio/wav" />
              <source src={mediaUrl} type="audio/mp3" />
              Your browser does not support the audio element.
            </audio>
            {!loading && chapters.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <Text strong style={{ display: 'block', marginBottom: 6 }}>
                  Jump to question
                </Text>
                <List
                  size="small"
                  bordered
                  style={{ maxHeight: 240, overflowY: 'auto' }}
                  dataSource={chapters}
                  rowKey="n"
                  renderItem={(chapter) => (
                    <List.Item
                      onClick={() => jumpToChapter(chapter)}
                      style={{
                        cursor: 'pointer',
                        background: activeChapter === chapter.n ? '#f0f5ff' : undefined,
                      }}
                    >
                      <Text strong style={{ marginRight: 10, flex: 'none' }}>
                        {formatOffset(chapter.offsetMs)}
                      </Text>
                      <Text
                        ellipsis={{ tooltip: chapter.text }}
                        style={{ flex: 1 }}
                      >
                        {`Q${chapter.n}. ${chapter.text}`}
                      </Text>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </>
        ) : (
          <video
            key={mediaUrl}
            controls
            autoPlay
            style={{ width: '100%', display: loading ? 'none' : 'block' }}
            onLoadedData={handleLoadedData}
            onError={handleError}
            crossOrigin="anonymous"
          >
            <source src={mediaUrl} type="video/webm" />
            <source src={mediaUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )
      ) : null}

      {showMultiNav && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 16,
            gap: 12,
          }}
        >
          <Button
            icon={<LeftOutlined />}
            onClick={goPrev}
            disabled={safeIndex === 0}
          >
            Previous
          </Button>
          <Text strong>
            {`${itemLabel} ${safeIndex + 1} of ${totalCount}`}
          </Text>
          <Button
            onClick={goNext}
            disabled={safeIndex >= totalCount - 1}
          >
            Next <RightOutlined />
          </Button>
        </div>
      )}
    </Modal>
  )
}

export default AudioVideoModal
